import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
} from "@mui/material";
import PdfViewer from "../components/PdfViewer";
import DataFieldsForm from "../components/DataFieldsForm";
import SignaturePad from "../components/SignaturePad";
import {
  getDocumentByIdAndSigner,
  signDocument,
  initTenant,
} from "../api/documents";
import Logo from "../assets/logo.png";
import LoadingScreen from "../components/LoadingScreen";
import SuccessScreen from "../components/SuccessScreen";
import ErrorScreen from "../components/ErrorScreen";
import AlreadySignedScreen from "../components/AlreadySignedScreen";

export default function SignDocument() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const signaturePadRef = useRef(null);

  const [document, setDocument] = useState(null);
  const [name, setName] = useState("");
  const [filledData, setFilledData] = useState({});
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState("");

  const documentId = searchParams.get("documentId");
  const signerToken = searchParams.get("signer");
  const clientId = searchParams.get("clientId");

  useEffect(() => {
    const fetchDoc = async () => {
      if (!documentId || !signerToken) {
        setError("Invalid link");
        setLoading(false);
        return;
      }

      try {
        await initTenant(clientId);
        const doc = await getDocumentByIdAndSigner(documentId, signerToken);
        setDocument(doc || null);
      } catch (e) {
        console.error(e);
        setError("Failed to load document");
      } finally {
        setLoading(false);
      }
    };
    fetchDoc();
  }, [documentId, signerToken, clientId]);

  const handleSign = async () => {
    if (!name.trim()) return alert("Please enter your name.");

    const signature = signaturePadRef.current?.getSignature();
    if (!signature || signaturePadRef.current.isEmpty()) {
      return alert("Please provide your signature before signing.");
    }

    try {
      await signDocument(documentId, signerToken, name, filledData, signature);
      setSigned(true);
    } catch (e) {
      console.error(e);
      alert("Failed to sign document.");
    }
  };

  const signerRole = document?.signer?.role;
  const fieldsForSigner = useMemo(
    () =>
      document?.dataToFill?.fields?.filter(
        (f) => !f.role || f.role === signerRole
      ) || [],
    [document, signerRole]
  );
  // ----- LOADING -----
  if (loading) return <LoadingScreen />;

  // ----- ERROR -----
  if (error) return <ErrorScreen title={error} onClick={() => navigate("/")} />;

  // ----- ALREADY SIGNED -----
  if (document.signer?.status === "signed")
    return <AlreadySignedScreen document={document} />;

  // ----- SUCCESSFULLY SIGNED -----
  if (signed) return <SuccessScreen onClick={() => navigate("/")} />;

  // ----- DEFAULT SIGN FLOW -----
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(to bottom right, #7AB2D3, #b3e5fc)",
        py: 4,
        px: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <img
          src={Logo}
          alt="OceanLogix"
          style={{ width: 160, marginBottom: 12 }}
        />
        <Typography variant="h4" fontWeight="bold">
          eSign by OceanLogix
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Sign your document securely and easily below.
        </Typography>
      </Box>

      {/* PDF Viewer */}
      <Paper
        elevation={4}
        sx={{
          width: "100%",
          maxWidth: 900,
          p: 2,
          mb: 4,
          backgroundColor: "#fafafa",
          borderRadius: 3,
        }}
      >
        <Typography variant="h6" mb={2}>
          Document: {document?.documentName}
        </Typography>
        {document?.downloadUrl && <PdfViewer url={document.downloadUrl} />}
      </Paper>

      {/* Form Section */}
      <Paper
        elevation={4}
        sx={{
          width: "100%",
          maxWidth: 700,
          p: 3,
          borderRadius: 3,
          backgroundColor: "#ffffff",
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSign();
          }}
        >
          <Typography variant="h6" gutterBottom>
            Signer: {signerRole}
          </Typography>

          <TextField
            label="Your Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            sx={{ mb: 3 }}
          />

          {fieldsForSigner.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={500} mb={1}>
                Additional Information
              </Typography>
              <DataFieldsForm
                fields={fieldsForSigner}
                values={filledData}
                onChange={setFilledData}
              />
              <Divider sx={{ my: 3 }} />
            </>
          )}

          <Typography variant="subtitle1" fontWeight={500} mb={1}>
            Signature
          </Typography>
          <SignaturePad ref={signaturePadRef} />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 3, px: 4, py: 1.2, fontWeight: 600 }}
          >
            Sign Document
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
